import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiMinus, FiInfo, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { MdPayments } from 'react-icons/md';
import { paymentService } from '../services';

interface BillEstimate {
  average: number;
  min: number;
  max: number;
  lastPayment: number;
  count: number;
  trend: 'up' | 'down' | 'stable';
}

interface ServiceWithHistory {
  serviceId: string;
  serviceName: string;
  serviceLogo?: string;
  referencias: string[];
  paymentCount: number;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-CV', {
    style: 'decimal',
    minimumFractionDigits: 0,
  }).format(value) + '$';
};

const TrendIcon: React.FC<{ trend: BillEstimate['trend'] }> = ({ trend }) => {
  switch (trend) {
    case 'up':
      return <FiTrendingUp className="text-red-500" />;
    case 'down':
      return <FiTrendingDown className="text-green-500" />;
    case 'stable':
      return <FiMinus className="text-pagali-gray-400" />;
  }
};

const TrendLabel: React.FC<{ trend: BillEstimate['trend'] }> = ({ trend }) => {
  switch (trend) {
    case 'up':
      return <span className="text-red-500">A subir</span>;
    case 'down':
      return <span className="text-green-500">A descer</span>;
    case 'stable':
      return <span className="text-pagali-gray-500">Estável</span>;
  }
};

const BillEstimateCard: React.FC = () => {
  const [services, setServices] = useState<ServiceWithHistory[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<BillEstimate | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const servicesWithHistory = paymentService.getServicesWithHistory();
    setServices(servicesWithHistory);

    // Auto-select first service if available
    if (servicesWithHistory.length > 0 && !selectedService) {
      setSelectedService(servicesWithHistory[0].serviceId);
    }
  }, []);

  useEffect(() => {
    if (selectedService) {
      const billEstimate = paymentService.getBillEstimate(selectedService, selectedRef || undefined);
      setEstimate(billEstimate);
    }
  }, [selectedService, selectedRef]);

  // Get current service
  const currentService = services.find(s => s.serviceId === selectedService);

  if (services.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-pagali-primary/5 to-pagali-purple/5 rounded-xl border border-pagali-gray-100 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-pagali-primary/10 flex items-center justify-center">
            <FiInfo className="text-pagali-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-pagali-gray-800">Estimativa de Contas</h3>
            <p className="text-sm text-pagali-gray-500">
              Baseado no seu histórico de pagamentos
            </p>
          </div>
        </div>
        {isExpanded ? (
          <FiChevronUp className="text-pagali-gray-400" />
        ) : (
          <FiChevronDown className="text-pagali-gray-400" />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          {/* Service Selector */}
          <div className="flex flex-wrap gap-2">
            {services.map((service) => (
              <button
                key={service.serviceId}
                onClick={() => {
                  setSelectedService(service.serviceId);
                  setSelectedRef(null);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                  ${selectedService === service.serviceId
                    ? 'bg-pagali-primary text-white'
                    : 'bg-white text-pagali-gray-700 hover:bg-pagali-gray-50'
                  }`}
              >
                {service.serviceLogo ? (
                  <img
                    src={`data:image/png;base64,${service.serviceLogo}`}
                    alt=""
                    className="w-5 h-5 rounded object-contain"
                  />
                ) : (
                  <MdPayments className="text-sm" />
                )}
                {service.serviceName}
              </button>
            ))}
          </div>

          {/* Reference Selector (if multiple references) */}
          {currentService && currentService.referencias.length > 1 && (
            <div>
              <label className="text-xs text-pagali-gray-500 mb-1 block">Referência:</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedRef(null)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition-all
                    ${!selectedRef
                      ? 'bg-pagali-purple text-white'
                      : 'bg-white text-pagali-gray-600 hover:bg-pagali-gray-50'
                    }`}
                >
                  Todas
                </button>
                {currentService.referencias.map((ref) => (
                  <button
                    key={ref}
                    onClick={() => setSelectedRef(ref)}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-all
                      ${selectedRef === ref
                        ? 'bg-pagali-purple text-white'
                        : 'bg-white text-pagali-gray-600 hover:bg-pagali-gray-50'
                      }`}
                  >
                    {ref}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Estimate Display */}
          {estimate ? (
            <div className="bg-white rounded-xl p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-pagali-gray-50 rounded-lg">
                  <p className="text-xs text-pagali-gray-500 mb-1">Média</p>
                  <p className="text-xl font-bold text-pagali-gray-800">
                    {formatCurrency(estimate.average)}
                  </p>
                </div>
                <div className="text-center p-3 bg-pagali-gray-50 rounded-lg">
                  <p className="text-xs text-pagali-gray-500 mb-1">Último</p>
                  <p className="text-xl font-bold text-pagali-gray-800">
                    {formatCurrency(estimate.lastPayment)}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="text-pagali-gray-500">
                    Min: <span className="font-medium text-pagali-gray-700">{formatCurrency(estimate.min)}</span>
                  </span>
                  <span className="text-pagali-gray-500">
                    Max: <span className="font-medium text-pagali-gray-700">{formatCurrency(estimate.max)}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendIcon trend={estimate.trend} />
                  <TrendLabel trend={estimate.trend} />
                </div>
              </div>

              <p className="text-xs text-pagali-gray-400 mt-3 text-center">
                Baseado em {estimate.count} pagamento{estimate.count !== 1 ? 's' : ''}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-6 text-center">
              <p className="text-pagali-gray-500 text-sm">
                Sem dados suficientes para estimativa
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BillEstimateCard;
